import React, { useState, useRef, useEffect } from "react";

/**
 * SearchableSelect — a dropdown with built-in text search.
 *
 * Props:
 *   options       — [{ value, label }]
 *   value         — currently selected value (string/number)
 *   onChange      — (value) => void
 *   placeholder   — text shown when nothing is selected
 *   searchPlaceholder — text inside the search input
 *   disabled      — bool
 *   isInvalid     — bool (shows Bootstrap invalid state)
 *   loading       — bool (shows spinner instead of options)
 *   emptyMessage  — text when no options match the search
 */
const SearchableSelect = ({
  options = [],
  value,
  onChange,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  disabled = false,
  isInvalid = false,
  loading = false,
  emptyMessage = "No options found",
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  const selected = options.find((o) => String(o.value) === String(value));

  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    if (open && searchRef.current) {
      searchRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (optValue) => {
    onChange(optValue);
    setOpen(false);
    setSearch("");
  };

  const handleToggle = () => {
    if (disabled) return;
    setOpen((prev) => !prev);
    if (open) setSearch("");
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <button
        type="button"
        className={`form-select text-start d-flex align-items-center justify-content-between ${isInvalid ? "is-invalid" : ""} ${disabled ? "disabled" : ""}`}
        style={{ cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.65 : 1 }}
        onClick={handleToggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={selected ? "text-body" : "text-muted"}>
          {selected ? selected.label : placeholder}
        </span>
        {loading && (
          <span className="spinner-border spinner-border-sm ms-auto text-secondary" role="status" />
        )}
      </button>

      {open && (
        <div
          className="border rounded shadow-sm bg-white"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 1050,
            maxHeight: 280,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div className="p-2 border-bottom">
            <div className="input-group input-group-sm">
              <span className="input-group-text bg-white border-end-0">
                <i className="fas fa-search text-muted" style={{ fontSize: "0.75rem" }} />
              </span>
              <input
                ref={searchRef}
                type="text"
                className="form-control border-start-0 ps-0"
                placeholder={searchPlaceholder}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          <ul
            className="list-unstyled mb-0 overflow-auto"
            role="listbox"
            style={{ maxHeight: 200 }}
          >
            {loading ? (
              <li className="text-center py-3 text-muted">
                <span className="spinner-border spinner-border-sm me-2" role="status" />
                <small>Loading…</small>
              </li>
            ) : filtered.length === 0 ? (
              <li className="text-center py-3 text-muted">
                <small>{emptyMessage}</small>
              </li>
            ) : (
              filtered.map((o) => (
                <li key={o.value}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={String(o.value) === String(value)}
                    className={`btn btn-sm w-100 text-start rounded-0 px-3 py-2 ${
                      String(o.value) === String(value)
                        ? "btn-primary"
                        : "btn-light"
                    }`}
                    onClick={() => handleSelect(o.value)}
                  >
                    {o.label}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
